import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FaBullseye,
  FaFlag,
  FaCheck,
  FaExclamationTriangle,
  FaPauseCircle,
  FaSyncAlt,
  FaPlus,
  FaArrowRight,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechBar,
  TechBarFill,
  TechChip,
  TechButton,
  TechButtonGhost,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  StatusPill,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { listObjectives } from '../../services/platformService';

const KRGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.85rem;
`;

const Card = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $color }) => $color};
  border-radius: 8px;
  padding: 1rem 1.15rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }

  h4 {
    margin: 0 0 0.5rem;
    color: var(--text-primary);
    font-size: 0.95rem;
    line-height: 1.35;
  }

  .meta {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.6rem;
  }
`;

const KrList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const KrItem = styled.li`
  display: grid;
  grid-template-columns: 1fr 70px;
  gap: 0.5rem;
  align-items: center;
  padding: 0.4rem 0.6rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  font-size: 0.84rem;
  color: var(--text-secondary);

  .val {
    font-family: ${mono};
    font-size: 0.7rem;
    text-align: right;
    color: var(--text-disabled);
  }
`;

const STATUS_META = {
  active: { color: accent, label: 'Activo' },
  at_risk: { color: 'var(--alert-error)', label: 'En riesgo' },
  paused: { color: 'var(--alert-warning)', label: 'Pausado' },
  done: { color: 'var(--alert-success)', label: 'Cumplido' },
};

const OKRs = () => {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  const load = () => {
    setLoadState({ loading: true, error: null });
    listObjectives()
      .then((data) => { setItems(data); setLoadState({ loading: false, error: null }); })
      .catch((e) => setLoadState({ loading: false, error: e?.message || 'Error' }));
  };

  useEffect(load, []);

  // Solo objetivos con KRs (esos son OKRs)
  const okrs = useMemo(
    () => items.filter((it) => Array.isArray(it.key_results) && it.key_results.length > 0),
    [items]
  );

  const stats = useMemo(() => {
    const totalKRs = okrs.reduce((acc, o) => acc + (o.key_results?.length || 0), 0);
    const completedKRs = okrs.reduce(
      (acc, o) => acc + (o.key_results || []).filter((kr) => kr.target && kr.current >= kr.target).length,
      0
    );
    const avgProgress = okrs.length > 0
      ? Math.round(okrs.reduce((a, o) => a + (o.progress || 0), 0) / okrs.length)
      : 0;
    return { total: okrs.length, totalKRs, completedKRs, avgProgress };
  }, [okrs]);

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader title="OKRs y Experimentos" dossier="OKR://EXP/v2" />
        <Loading>Cargando OKRs reales</Loading>
      </TechShell>
    );
  }
  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="OKRs y Experimentos" dossier="OKR://EXP/v2" />
        <ErrorBox>No se pudo cargar · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="OKRs y Experimentos"
        subtitle="Objetivos con resultados medibles, guardados y compartidos con el equipo"
        dossier="OKR://EXP/v2"
        actions={
          <>
            <TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>
            <TechButton as={Link} to="/objectives">
              <FaPlus /> Crear OKR
            </TechButton>
          </>
        }
      >
        <StatusPill>OKRs <strong>{stats.total}</strong></StatusPill>
        <StatusPill>KRs totales <strong>{stats.totalKRs}</strong></StatusPill>
        <StatusPill>KRs completados <strong>{stats.completedKRs}</strong></StatusPill>
      </TechPageHeader>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaFlag /> Objetivos con KRs</StatLabel>
          <StatValue>{stats.total}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel><FaBullseye /> KRs totales</StatLabel>
          <StatValue>{stats.totalKRs}</StatValue>
          <StatSub>{stats.completedKRs} cumplidos</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCheck /> Tasa de cumplimiento</StatLabel>
          <StatValue>
            {stats.totalKRs > 0 ? Math.round((stats.completedKRs / stats.totalKRs) * 100) : 0}%
          </StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Progreso promedio</StatLabel>
          <StatValue>{stats.avgProgress}%</StatValue>
        </StatTile>
      </StatStrip>

      {okrs.length === 0 ? (
        <TechSection title="Sin OKRs cargados">
          <Empty>
            Un OKR es un objetivo con al menos un Key Result medible.
            <br />
            Creá uno desde <strong>Objetivos</strong> agregando KRs con valor actual y meta.
          </Empty>
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <TechButton as={Link} to="/objectives">
              Ir a Objetivos <FaArrowRight />
            </TechButton>
          </div>
        </TechSection>
      ) : (
        <TechSection title={`OKRs activos · ${okrs.length}`}>
          <KRGrid>
            {okrs.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.active;
              const Icon = o.status === 'done' ? FaCheck : o.status === 'at_risk' ? FaExclamationTriangle : o.status === 'paused' ? FaPauseCircle : FaFlag;
              return (
                <Card key={o.id} $color={meta.color}>
                  <h4>{o.title}</h4>
                  <div className="meta">
                    <TechChip>{o.category}</TechChip>
                    <span style={{ color: meta.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Icon /> {meta.label}
                    </span>
                    <span>progreso · {o.progress || 0}%</span>
                  </div>
                  <TechBar style={{ marginBottom: 10 }}>
                    <TechBarFill value={o.progress || 0} color={meta.color} />
                  </TechBar>
                  <KrList>
                    {(o.key_results || []).map((kr, i) => {
                      const pct = kr.target ? Math.min(100, Math.round((kr.current / kr.target) * 100)) : 0;
                      return (
                        <KrItem key={i}>
                          <div>
                            <div style={{ marginBottom: 3 }}>{kr.title}</div>
                            <TechBar style={{ height: 4 }}>
                              <TechBarFill value={pct} />
                            </TechBar>
                          </div>
                          <span className="val">{kr.current}/{kr.target}</span>
                        </KrItem>
                      );
                    })}
                  </KrList>
                </Card>
              );
            })}
          </KRGrid>
        </TechSection>
      )}
    </TechShell>
  );
};

export default OKRs;
